import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BadgeCheck, Factory, FileText, ShieldCheck, Wrench } from "lucide-react";
import { toast } from "sonner";
import { RepairCard, RepairDialog, useNetwork, useRepairs } from "@/components/household/RepairsPanel";
import { DocumentsPanel } from "@/components/household/DocumentsPanel";
import { ClaimDialog, useInsurance } from "@/components/household/InsurancePanel";
import { Button } from "@/components/ui/button";
import { useHousehold } from "@/context/HouseholdContext";
import { useHouseholdDetail } from "@/hooks/use-household-detail";
import { api, formatApiError } from "@/lib/api";

const Box = ({ icon: Icon, title, children, testid, action }) => (
  <div className="card-surface space-y-3 px-5 py-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-sm font-semibold"><Icon className="h-4 w-4" /><span className="flex-1">{title}</span>{action}</div>
    {children}
  </div>
);

const Manufacturer = ({ a }) => {
  const { active } = useHousehold();
  const qc = useQueryClient();
  const { canEdit } = useHouseholdDetail();
  const { data: makers = [] } = useNetwork("manufacturer");
  const regs = useQuery({ queryKey: ["registrations", active.id], queryFn: () => api.get(`/households/${active.id}/registrations`).then((r) => r.data) });
  const maker = makers.find((o) => (o.brands || []).some((b) => b.toLowerCase() === (a.brand || "").toLowerCase()));
  if (!maker) return null;
  const reg = (regs.data || []).find((r) => r.asset_id === a.id && r.org_id === maker.id);
  const register = async () => {
    try {
      await api.post(`/households/${active.id}/assets/${a.id}/register`, { org_id: maker.id });
      ["registrations", "asset", "assets", "dashboard"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      toast.success(`Produit enregistré auprès de ${maker.name}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Box icon={Factory} title={`Garantie fabricant · ${maker.name}`} testid="asset-manufacturer">
      {reg ? <p className="flex items-center gap-2 text-sm" data-testid="asset-registered"><BadgeCheck className="h-4 w-4 text-success" />Produit enregistré{reg.warranty_end && ` · garantie constructeur jusqu'au ${new Date(reg.warranty_end).toLocaleDateString("fr-FR")}`}</p>
        : <div className="flex flex-wrap items-center gap-3 text-sm"><span className="flex-1 text-muted-foreground">{maker.description}</span>
          {canEdit && <Button size="sm" className="rounded-full" onClick={register} data-testid="asset-register-button">Enregistrer le produit</Button>}</div>}
      <p className="text-xs text-muted-foreground">Le fabricant reçoit uniquement le produit (modèle, n° de série, date d'achat), jamais votre identité.</p>
    </Box>
  );
};

const Coverage = ({ a }) => {
  const { canEdit } = useHouseholdDetail();
  const contracts = (useInsurance().data || []).filter((c) => c.asset_ids.includes(a.id));
  const [claim, setClaim] = useState(null);
  return (
    <Box icon={ShieldCheck} title="Assurance" testid="asset-insurance">
      {contracts.length ? contracts.map((c) => (
        <div key={c.id} className="flex flex-wrap items-center gap-2 text-sm">
          <span className="flex-1">Couvert par {c.org_name} · n° {c.number}</span>
          {canEdit && <Button size="sm" variant="outline" className="rounded-full" onClick={() => setClaim(c)} data-testid={`asset-claim-${c.id}`}>Déclarer un sinistre</Button>}
        </div>
      )) : <p className="text-sm text-muted-foreground">Aucun contrat ne couvre cet équipement. Ajoutez-le depuis Mon Foyer › Assurances.</p>}
      {claim && <ClaimDialog contract={claim} assetId={a.id} onOpenChange={() => setClaim(null)} />}
    </Box>
  );
};

const Repairs = ({ a }) => {
  const { canEdit } = useHouseholdDetail();
  const repairs = (useRepairs().data || []).filter((r) => r.asset_id === a.id);
  const [open, setOpen] = useState(false);
  return (
    <Box icon={Wrench} title="Réparations (SAV)" testid="asset-repairs"
      action={canEdit && <Button size="sm" className="rounded-full" onClick={() => setOpen(true)} data-testid="asset-repair-button">Demander une réparation</Button>}>
      {repairs.length ? <ul className="space-y-3">{repairs.map((r) => <RepairCard key={r.id} r={r} canEdit={canEdit} />)}</ul> : <p className="text-sm text-muted-foreground">Aucune demande.</p>}
      {open && <RepairDialog open onOpenChange={setOpen} assetId={a.id} />}
    </Box>
  );
};

export const AssetLinks = ({ a }) => (
  <div className="space-y-4">
    <Manufacturer a={a} />
    <Coverage a={a} />
    <Repairs a={a} />
    <Box icon={FileText} title="Documents liés" testid="asset-documents-box"><DocumentsPanel assetId={a.id} /></Box>
  </div>
);
